import { CLIENT_LOG_BATCH_SIZE } from "../../src/config/client-log.js";
import { ROUTES } from "../../src/routes/constants.js";

const LOG_ENDPOINT = ROUTES.API.CLIENT_LOG;
const BATCH_INTERVAL_MS = 5_000;
const MAX_QUEUE_SIZE = 100;

/** @type {Array<{level: string, message: string, timestamp: string, url: string, stack?: string}>} */
let queue = [];
let flushTimer = null;

function now() {
  return new Date().toISOString();
}

function currentUrl() {
  try {
    const loc = globalThis.location;
    if (!loc?.href) return "";
    return `${loc.origin}${loc.pathname}`.slice(0, 2000);
  } catch {
    return "";
  }
}

/**
 * Enqueue a log entry. Automatically flushes when the batch is full.
 */
function enqueue(level, message, stack) {
  if (queue.length >= MAX_QUEUE_SIZE) return; // drop to prevent memory blowup

  queue.push({
    level,
    message: String(message).slice(0, 2000),
    timestamp: now(),
    url: currentUrl(),
    ...(stack && { stack: String(stack).slice(0, 4000) }),
  });

  if (queue.length >= CLIENT_LOG_BATCH_SIZE) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, BATCH_INTERVAL_MS);
  }
}

/**
 * Send queued logs to the server via Beacon API (fire-and-forget).
 * Falls back to fetch for environments without Beacon support.
 */
function flush() {
  clearTimeout(flushTimer);
  flushTimer = null;

  if (queue.length === 0) return;

  const batch = queue;
  queue = [];

  const payload = JSON.stringify(batch);

  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    const queued = navigator.sendBeacon(LOG_ENDPOINT, blob);
    if (queued) {
      return;
    }
  }

  fetch(LOG_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {}); // best-effort
}

/**
 * Install global handlers for uncaught errors and unhandled rejections.
 */
function installGlobalHandlers() {
  globalThis.addEventListener("error", (event) => {
    enqueue("error", event.message || "Unknown error", event.error?.stack);
  });

  globalThis.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason?.message ||
      (reason == null ? "Unhandled promise rejection" : String(reason));
    enqueue("error", message, reason?.stack);
  });

  // Flush remaining logs when the page is being unloaded
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });
}

/**
 * Wrap console.error and console.warn so calls are automatically captured.
 * Original console behaviour is preserved.
 */
function wrapConsole() {
  for (const level of ["error", "warn"]) {
    const original = console[level];
    console[level] = (...args) => {
      original.apply(console, args);

      const first = args[0];
      if (first instanceof Error) {
        enqueue(level, first.message, first.stack);
      } else {
        enqueue(level, args.map(String).join(" "));
      }
    };
  }
}

// --- Public API ---

export const logger = {
  error(message, extra) {
    const stack = extra instanceof Error ? extra.stack : extra?.stack;
    enqueue("error", message, stack);
  },
  warn(message) {
    enqueue("warn", message);
  },
  info(message) {
    enqueue("info", message);
  },
  /** Force-send any buffered logs immediately. */
  flush,
};

let initialized = false;

/**
 * Initialise client-side logging. Call once at app startup.
 * - Installs global error/rejection handlers
 * - Wraps console.error & console.warn
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initLogger() {
  if (initialized) return;
  initialized = true;

  installGlobalHandlers();
  wrapConsole();
}
